<template>
    <v-container>
        <v-row align="center" class="mb-4">
            <v-col>
                <h1 class="font-weight-bold mt-10 title-page">Resumo do Credor</h1>
                <p class="text-subtitle-1 grey--text">Apresenta uma visão geral das suas informações.
                </p>
            </v-col>
            <v-col cols="auto">
                <v-btn id="btnBack" outlined color="primary" to="/home">
                    <v-icon left>mdi-arrow-left</v-icon> Voltar
                </v-btn>
            </v-col>
        </v-row>

        <v-row v-if="dataset.loading" justify="center" class="my-10">
            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        </v-row>

        <div v-else>
            <div v-if="!$root.user" v-html="dataset.contentUserAnonymous"></div>

            <template v-else>
                <v-row>
                    <v-col cols="12" sm="6" md="4">
                        <v-card elevation="2" class="rounded-lg pa-4 border-left-valor">
                            <div class="text-overline mb-1 grey--text">
                                {{ dataset.summary.creditor_list.is_multi_class ? 'Valor Total' : 'Valor' }} - Lista de
                                Credores
                            </div>

                            <template
                                v-if="dataset && dataset.summary && dataset.summary.creditor_list && dataset.summary.creditor_list.amount">
                                <div class="text-h5 font-weight-bold primary--text">
                                    {{ formatCurrency(dataset.summary.creditor_list.amount) }}
                                </div>

                                <div class="mt-2" v-if="dataset.summary.creditor_list.type">
                                    <v-chip v-for="clsId in dataset.summary.creditor_list.type" :key="clsId" x-small
                                        outlined class="mr-1 mt-1">
                                        {{ getClassName(clsId) }}
                                    </v-chip>
                                </div>
                            </template>

                            <template v-else>
                                <div class="orange--text text-button font-weight-bold d-block mt-1">
                                    Não consta na lista
                                </div>
                                <div class="text-caption grey--text">
                                    Nenhum crédito identificado para este {{ dataset.summary.user.is_company ? 'CNPJ' :
                                    'CPF' }}
                                </div>
                            </template>
                        </v-card>
                    </v-col>

                    <v-col cols="12" sm="6" md="4">
                        <v-card elevation="2" class="rounded-lg pa-4 border-left-processo" height="100%">
                            <div class="text-overline mb-1 grey--text">Processo(s) em Andamento</div>

                            <template v-if="processDetails.count !== 'Nenhum' && processDetails.count > 0">
                                <div class="d-flex align-center">
                                    <v-icon left color="primary">mdi-gavel</v-icon>
                                    <div class="text-h5 font-weight-bold">
                                        {{ processDetails.count }}
                                    </div>
                                </div>

                                <div class="mt-2" v-if="processDetails.locations.length > 0">
                                    <div class="d-flex flex-wrap align-center">
                                        <span class="text-caption grey--text text--darken-1 font-weight-bold mr-2 mb-1">
                                            Local:
                                        </span>
                                        <v-chip v-for="(local, index) in processDetails.locations" :key="index" x-small
                                            class="mr-1 mb-1 blue lighten-5 blue--text text--darken-4" label>
                                            <v-icon left x-small>mdi-map-marker</v-icon>
                                            {{ local }}
                                        </v-chip>
                                    </div>
                                </div>
                            </template>

                            <template v-else>
                                <div class="grey--text text-button font-weight-bold d-block mt-1">
                                    Nenhum
                                </div>
                                <div class="text-caption grey--text">
                                    Processo judicial em curso cadastrado
                                </div>
                            </template>
                        </v-card>
                    </v-col>

                    <v-col cols="12" sm="6" md="4">
                        <v-card elevation="2" class="rounded-lg pa-4" height="100%">
                            <div class="text-overline mb-1 grey--text">Adesão ao Plano</div>
                            <div class="mt-1">
                                <v-chip
                                    v-if="dataset && dataset.summary && dataset.summary.plan_adherence && dataset.summary.plan_adherence"
                                    :color="adherenceColor(dataset.summary.plan_adherence.status)" dark small>
                                    {{ getAdherenceText(dataset.summary.plan_adherence) }}
                                </v-chip>
                                <div v-else class="text-button grey--text">Adesão não registrada</div>
                            </div>
                            <div v-if="dataset.summary.plan_adherence
                                && (dataset.summary.plan_adherence.status === 0
                                    || dataset.summary.plan_adherence.status === 2)" class="mt-3">
                                <router-link :to="{ path: '/adesao-plano' }"
                                    class="primary--text text-caption font-weight-medium">
                                    Continuar adesão
                                </router-link>
                            </div>

                        </v-card>
                    </v-col>
                </v-row>

                <v-row v-if="certificateFeatureEnabled && $modules.isVisible('site.credit_certificate.issue')" class="mt-4">
                    <ModuleClosedAlert module="site.credit_certificate.issue" alert-class="mx-3 mt-3 mb-0" />
                    <v-col cols="12">
                        <v-card outlined class="pa-4 rounded-lg">
                            <div class="d-flex align-center flex-wrap">
                                <div>
                                    <div class="text-overline mb-1 grey--text text--darken-1">Certidão de Participação</div>
                                    <div v-if="certificate.listed" class="text-body-2">
                                        Baixe a certidão emitida pela Administração Judicial.
                                    </div>
                                    <div v-else class="text-body-2 grey--text">
                                        {{ certificate.message || 'Você não está na lista de credores.' }}
                                    </div>
                                </div>
                                <v-spacer />
                                <v-select v-if="certificate.classes.length > 1" class="certificate-class-select mr-md-3 mt-3 mt-md-0"
                                    dense outlined hide-details="auto" label="Classe" :items="certificate.classes"
                                    item-text="label" return-object v-model="certificate.selected" />
                                <v-btn v-if="!certificate.selected || !certificate.selected.issue_blocked || isReviewRequired(certificate.selected)"
                                    color="primary" elevation="0" class="mt-3 mt-md-0"
                                    :disabled="!$modules.isOpen('site.credit_certificate.issue') || !certificate.selected || certificate.downloading || (!isReviewRequired(certificate.selected) && (!certificate.canIssue || isCertificateBlocked(certificate.selected)))"
                                    @click="downloadCertificate('creditor_summary')">
                                    <v-icon left>mdi-download-outline</v-icon>
                                    Baixar certidão
                                </v-btn>
                            </div>
                            <v-alert v-if="certificate.selected && isCertificateBlocked(certificate.selected) && !isReviewRequired(certificate.selected)" type="warning" dense text class="mt-3 mb-0">
                                {{ certificate.selected.issue_block_reason || 'Esta certidão está vencida ou invalidada. O download está bloqueado.' }}
                            </v-alert>
                            <div v-else-if="certificate.selected && certificate.selected.valid_until" class="text-caption grey--text mt-3">
                                Válida até {{ formatDate(certificate.selected.valid_until) }}.
                            </div>
                            <CertificateReviewRequiredDialog v-model="certificate.reviewRequiredDialog" />
                        </v-card>
                    </v-col>
                </v-row>

                <v-row class="mt-4">
                    <v-col cols="12">
                        <v-card outlined class="rounded-lg">
                            <v-toolbar flat dense color="grey lighten-4">
                                <v-toolbar-title class="text-subtitle-1 font-weight-bold">Informações de
                                    Cadastro</v-toolbar-title>
                            </v-toolbar>
                            <v-divider></v-divider>

                            <v-row class="pa-4" no-gutters>
                                <v-col cols="12" md="6" class="pr-md-4 border-right-md">
                                    <div class="d-flex align-center mb-2 flex-wrap">
                                        <span
                                            class="text-caption font-weight-bold grey--text text-uppercase mr-2">Credor</span>

                                        <v-chip x-small label outlined class="mr-2 grey--text text--darken-2">
                                            {{ getCreditorType(dataset.summary.user.registration) }}
                                        </v-chip>

                                        <v-chip x-small
                                            :color="dataset.summary.user.is_profile_complete ? 'success' : 'warning'"
                                            dark>
                                            {{ dataset.summary.user.is_profile_complete ? 'Cadastro Completo' : 'Cadastro com Pendências' }}
                                        </v-chip>

                                        <router-link v-if="!dataset.summary.user.is_profile_complete"
                                            :to="{ path: '/minha-conta' }"
                                            class="primary--text text-caption font-weight-medium ml-5">
                                            Corrigir pendência(s)
                                        </router-link>

                                    </div>

                                    <div class="text-h6 mb-0">{{ dataset.summary.user.name }}</div>
                                    <div class="text-body-2"><v-icon small class="mr-1">mdi-email-outline</v-icon> {{
                                        dataset.summary.user.email }}</div>
                                    <div class="text-body-2 mb-2">
                                        <v-icon small class="mr-1">
                                            mdi-card-account-details-outline
                                        </v-icon>
                                        {{ formatCpfCnpj(dataset.summary.user.registration) }}
                                    </div>

                                    <template v-if="getCreditorType(dataset.summary.user.registration) === 'PJ'">
                                        <v-divider class="my-3"></v-divider>

                                        <div class="text-caption font-weight-bold blue-grey--text text-uppercase mb-2">
                                            Representante(s) ({{ dataset.summary.user.representatives.length }})
                                        </div>

                                        <div
                                            v-if="dataset.summary.user.representatives && dataset.summary.user.representatives.length > 0">
                                            <v-card v-for="(rep, idx) in dataset.summary.user.representatives"
                                                :key="idx" flat outlined class="mb-2 pa-2 grey lighten-5">
                                                <v-row no-gutters align="center">
                                                    <v-col>
                                                        <div class="text-body-2 font-weight-medium line-height-tight">{{
                                                            rep.name }}</div>
                                                        <div class="text-caption grey--text">{{
                                                            formatCpfCnpj(rep.registration) }}</div>
                                                    </v-col>

                                                    <v-col cols="auto">
                                                        <v-tooltip bottom>
                                                            <template v-slot:activator="{ on, attrs }">
                                                                <v-chip v-bind="attrs" v-on="on" x-small
                                                                    :color="getRepresentativeStatus(rep.status).color"
                                                                    outlined>
                                                                    <v-icon left x-small>{{
                                                                        getRepresentativeStatus(rep.status).icon
                                                                        }}</v-icon>
                                                                    {{ getRepresentativeStatus(rep.status).text }}
                                                                </v-chip>
                                                            </template>
                                                            <span>Status da análise do representante</span>
                                                        </v-tooltip>
                                                    </v-col>
                                                </v-row>
                                            </v-card>
                                        </div>

                                        <v-alert v-else type="warning" dense text icon="mdi-account-plus"
                                            class="text-caption py-1">
                                            Nenhum representante cadastrado.
                                        </v-alert>
                                    </template>
                                </v-col>

                                <v-col cols="12" md="6" class="pl-md-4 mt-4 mt-md-0">
                                    <div class="d-flex align-center mb-2">
                                        <span
                                            class="text-caption font-weight-bold grey--text text-uppercase mr-2">Procurador</span>
                                        <template v-if="dataset.summary.lawyer && dataset.summary.lawyer.has_lawyer">
                                            <v-chip x-small
                                                :color="dataset.summary.lawyer.lawyer.is_profile_complete ? 'success' : 'warning'"
                                                dark>
                                                {{ dataset.summary.lawyer.lawyer.is_profile_complete ? 'Cadastro Completo' : 'Cadastro com Pendências' }}
                                            </v-chip>
                                        </template>
                                    </div>

                                    <template v-if="dataset.summary.lawyer && dataset.summary.lawyer.has_lawyer">
                                        <div class="text-h6 mb-0">{{ dataset.summary.lawyer.lawyer.name }}</div>
                                        <div class="text-body-2"><v-icon small class="mr-1">mdi-email-outline</v-icon>
                                            {{
                                                dataset.summary.lawyer.lawyer.email }}</div>
                                        <div class="text-body-2"><v-icon small
                                                class="mr-1">mdi-card-account-details-outline</v-icon> {{
                                                    formatCpfCnpj(dataset.summary.lawyer.lawyer.registration) }}</div>
                                    </template>
                                    <v-alert v-else type="info" dense text class="mt-2 mb-0">Nenhum procurador
                                        cadastrado.</v-alert>
                                </v-col>
                            </v-row>
                        </v-card>
                    </v-col>
                </v-row>

                <v-row class="mt-4">
                    <v-col cols="12" md="6">
                        <v-card outlined class="pa-4 rounded-lg fill-height">
                            <div class="d-flex justify-space-between align-center mb-4">
                                <div class="text-overline mb-1 grey--text text--darken-1">Habilitação / Divergência
                                </div>
                                <v-chip
                                    v-if="dataset && dataset.summary && dataset.summary.event && dataset.summary.event.items.length"
                                    small dark :color="getReviewColor(dataset.summary.event.event.status)">
                                    {{ getReviewText(dataset.summary.event.event.status) }}
                                </v-chip>
                            </div>
                            <div class="text-center py-4"
                                v-if="dataset && dataset.summary && dataset.summary.event && dataset.summary.event.items.length">
                                <div class="text-h4 font-weight-bold">{{
                                    formatCurrency(calculateTotal(dataset.summary.event.items)) }}
                                </div>
                                <div class="grey--text">Total Solicitado</div>
                            </div>
                            <v-alert v-else type="info" dense text>Nenhuma solicitação registrada.</v-alert>
                        </v-card>
                    </v-col>

                    <v-col cols="12" md="6">
                        <v-card outlined class="pa-4 rounded-lg fill-height">
                            <div class="d-flex justify-space-between align-center mb-4">
                                <div class="text-overline mb-1 grey--text text--darken-1">Revisão de Crédito</div>
                                <v-chip
                                    v-if="dataset && dataset.summary && dataset.summary.credit_review && dataset.summary.credit_review.items.length"
                                    small dark
                                    :color="getReviewColor(dataset.summary.credit_review.objectionEvent.status)">
                                    {{ getReviewText(dataset.summary.credit_review.objectionEvent.status) }}
                                </v-chip>
                            </div>
                            <div class="text-center py-4"
                                v-if="dataset && dataset.summary && dataset.summary.credit_review && dataset.summary.credit_review.items.length">
                                <div class="text-h4 font-weight-bold">{{
                                    formatCurrency(calculateTotal(dataset.summary.credit_review.items)) }}</div>
                                <div class="grey--text">Total Solicitado</div>
                            </div>
                            <v-alert v-else type="info" dense text>Nenhuma revisão registrada.</v-alert>
                        </v-card>
                    </v-col>
                </v-row>
            </template>
        </div>
    </v-container>
</template>

<script>
import 'moment/locale/pt-br'
import { eventHub } from '@/main'
import { cpf, cnpj } from 'cpf-cnpj-validator'
import axios from 'axios'
import moment from 'moment-timezone'
import { downloadPdfResponse, readBlobError } from '@/common/util/download-file'
import CertificateReviewRequiredDialog from '@/components/creditor/CertificateReviewRequiredDialog.vue'

export default {
    name: 'ResumoUsuario',
    components: { CertificateReviewRequiredDialog },
    data: () => ({
        certificateFeatureEnabled: true,
        user: null,
        loading: false,
        dataset: {
            summary: {
                user: {},
                credit_value: 0,
                hab_div: {},
                credit_review: {},
                plan_adherence: {}
            },
            creditorTypes: [
                { id: 1, name: 'Trabalhistas' },
                { id: 2, name: 'Garantia Real' },
                { id: 3, name: 'Quirografários' },
                { id: 4, name: 'ME/EPP' },
                { id: 5, name: 'Extraconcursais' },
                { id: 6, name: 'Ilíquidos' }
            ],
            contentUserAnonymous: '',
            contentUserLogged: '',
            loading: false,
            data: {}
        },
        selectedDoc: null,
        dialogViewDoc: false,
        certificate: {
            loading: false,
            downloading: false,
            listed: false,
            canIssue: false,
            message: '',
            classes: [],
            selected: null,
            reviewRequiredDialog: false
        },
    }),

    created() {
        this.$ga.event({
            eventCategory: 'page',
            eventAction: 'action',
            eventLabel: 'click',
            eventValue: 'user-area'
        })

        this.loadData()
    },

    computed: {
        processDetails() {
            const locations = [];
            let totalCount = 0;

            const extractData = (path) => {
                if (path && path.processes) {
                    totalCount += path.processes.length;
                    path.processes.forEach(p => {
                        if (p.local) locations.push(p.local);
                    });
                }
            };

            extractData(this.dataset && this.dataset.summary && this.dataset.summary.event && this.dataset.summary.event.event);
            extractData(this.dataset && this.dataset.summary && this.dataset.summary.credit_review && this.dataset.summary.credit_review.objectionEvent);
            const uniqueLocations = [...new Set(locations)].sort();

            return {
                count: totalCount > 0 ? totalCount : 'Nenhum',
                locations: uniqueLocations
            };
        }
    },

    methods: {

        async loadData() {

            await this.loadText()

            if (this.$root.user) {
                const requests = [this.loadSummaryData()]
                if (this.certificateFeatureEnabled) {
                    requests.push(this.loadCertificateClasses())
                }
                await Promise.all(requests)
            }
        },

        async loadSummaryData() {

            this.dataset.loading = true

            try {

                const result = await axios.get('/api/users/summary-information')

                this.dataset.summary = result.data.content

            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            } finally {
                this.dataset.loading = false
            }
        },

        async loadCertificateClasses() {
            this.certificate.loading = true

            try {
                const result = await axios.get('/api/issued-credit-certificates/classes')
                const content = result.data.content || {}
                const classes = (content.classes || []).map((item) => ({
                    ...item,
                    label: item.label || `${item.creditor_type_label} - ${item.amount_formatted}`
                }))

                this.certificate.listed = !!content.listed
                this.certificate.canIssue = !!content.can_issue
                this.certificate.message = content.message || ''
                this.certificate.classes = classes
                this.certificate.selected = classes.length ? classes[0] : null
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            } finally {
                this.certificate.loading = false
            }
        },

        async downloadCertificate(source) {
            if (this.$moduleBlocked('site.credit_certificate.issue')) return
            if (!this.certificate.selected) return
            if (this.isReviewRequired(this.certificate.selected)) {
                this.certificate.reviewRequiredDialog = true
                return
            }

            this.certificate.downloading = true

            try {
                const result = await axios.get('/api/issued-credit-certificates/download', {
                    params: {
                        creditor_type: this.certificate.selected.creditor_type,
                        currency: this.certificate.selected.currency,
                        source
                    },
                    responseType: 'blob'
                })

                await downloadPdfResponse(
                    result,
                    `certidao_participacao_${this.certificate.selected.auth_code || 'credor'}.pdf`,
                    'Certidão de Participação'
                )
                await this.loadCertificateClasses()
            } catch (error) {
                const blobError = await readBlobError(error)
                if (blobError && blobError.code === 'review_required') {
                    this.certificate.reviewRequiredDialog = true
                    return
                }

                this.$dialog.notify.error(blobError && blobError.message ? blobError.message : this.$codes(error), {
                    position: 'top-right',
                    timeout: 3000
                })
            } finally {
                this.certificate.downloading = false
            }
        },

        isReviewRequired(item) {
            return !!item && item.issue_block_code === 'review_required'
        },

        isCertificateBlocked(item) {
            if (!item) return true
            if (item.issue_blocked) return true
            if (item.invalidated) return true
            if (!item.valid_until) return false
            return new Date(item.valid_until) < new Date()
        },

        async loadText() {
            const result = await axios.get(`/api/dynamic-infos/keys`, {
                params: { keys: 'user-event-anonymous,user-event-logged' }
            })

            if (!result.data.content) return

            let contentUserAnonymous = result.data.content.find((x) => x.key === 'user-event-anonymous')
            let contentUserLogged = result.data.content.find((x) => x.key === 'user-event-logged')

            this.dataset.contentUserAnonymous = contentUserAnonymous ? contentUserAnonymous.value : ''
            this.dataset.contentUserLogged = contentUserLogged ? contentUserLogged.value : ''
        },

        // async checkAccess() {
        //     try {
        //         const response = await axios.get(`/api/users/check-access`)
        //     } catch (error) {
        //         if (error.response && error.response.status === 403) {
        //             this.$router.replace('/minha-conta')
        //             return
        //         }

        //         this.$dialog.notify.error(this.$codes(error), {
        //             position: 'top-right',
        //             timeout: 2000
        //         })
        //     }
        // },

        formatCurrency(value) {
            if (!value) return 'R$ 0,00'
            return value.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            })
        },

        formatDate(date) {
            return moment(date).format('DD/MM/YYYY HH:mm')
        },

        getCreditorType(registration) {
            if (!registration) return '';

            const clean = registration.toString().replace(/\D/g, '');
            return clean.length > 11 ? 'PJ' : 'PF';
        },

        getRepresentativeStatus(status) {
            const map = {
                0: { text: 'Pendente', color: 'orange', icon: 'mdi-clock-outline' },
                1: { text: 'Aprovado', color: 'success', icon: 'mdi-check-decagram' },
                2: { text: 'Rejeitado', color: 'error', icon: 'mdi-alert-circle' }
            };

            return map[status] || { text: 'Desconhecido', color: 'grey', icon: 'mdi-help' };
        },

        calculateTotal(items) {
            if (!items || !Array.isArray(items)) return 0;
            
            return items.reduce((total, item) => {
                const value = parseFloat(item.amount) || 0;
                return total + value;
            }, 0);
        },

        getClassName(id) {
            const typeEnum = this.dataset.creditorTypes.find(item => item.id == id);
            
            return typeEnum ? typeEnum.name : `Classe ${id}`;
        },

        formatCpfCnpj(value) {
            if (!value) {
                return ''
            }
            return value.length == 14 ? cnpj.format(value) : cpf.format(value);
        },

        getReviewText(status) {

            if (status === 0) return 'Pendente'
            if (status === 1) return 'Aprovado'
            if (status === 2) return 'Rejeitado'
            if (status === 3) return 'Aprovado Parcial'
            if (status === 4) return 'Documentos Solicitados'
            if (status === 5) return 'Documentos Atualizados'
            if (status === 6) return 'Contraproposta Enviada'
            if (status === 7) return 'Contraproposta Retornada'
            if (status == null) return 'Indisponível'

            return 'Desconecido'
        },

        getReviewColor(status) {
            if (status === 0) return 'warning'
            if (status === 1) return 'success'
            if (status === 2) return 'error'
            if (status === 3) return 'indigo darken-1'
            if (status === 4) return '#7E55AB'
            if (status === 5) return '#29B6F6'
            if (status === 6) return '#9E9D24'
            if (status === 7) return '#00695C'
            if (status == null) return 'gray'

            return
        },

        getAdherenceText(a) {
            if (!a || a.status == null) {
                return 'Você não aderiu ao plano'
            }

            if (a.status === 0) {
                return 'Você ainda não finalizou a adesão'
            }

            if (a.status === 1) {
                const date = a.updated_at
                    ? this.formatDate(a.updated_at)
                    : ''
                return `Você aderiu ao plano${date ? ' em ' + date : ''}`
            }

            if (a.status === 2) {
                return 'Você tentou aderir ao plano, mas houve algum erro'
            }

            return 'Status desconhecido'
        },

        adherenceColor(status) {
            if (status === 0) return 'warning'
            if (status === 1) return 'success'
            if (status === 2) return 'error'
            if (status == null) return 'gray'

            return 'grey'
        },

    }
}
</script>

<style lang="scss">
.title-page {
    color: var(--v-primary-base);
    font-family: 'Raleway';
    font-size: 40px;
    font-style: normal;
    font-weight: 700;
    line-height: 38px;
}

.section-info-title {
    color: var(--v-primary-base);
    font-family: 'Raleway';
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
}

.section-info-value {
    font-family: 'Raleway';
    font-size: 14px;
    font-style: normal;
}

.custom-panel-header {
    min-height: 20px !important;
    line-height: 20px !important;
    padding-top: 5 !important;
    padding-bottom: 0 !important;
}

.main-text {
    font-size: 16px;
    color: #000;
}

.border-left-valor { border-left: 5px solid #1976D2 !important; }
    @media (min-width: 960px) {
        .border-right-md { border-right: 1px solid #e0e0e0; }
}

.caption-text {
    font-size: 12px;
    font-weight: bold;
    color: #444;
    margin-top: -5px;
}

.line-height-tight {
  line-height: 1.2 !important;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.border-right-md {
  border-right: 1px solid #e0e0e0;
}

.certificate-class-select {
  max-width: 420px;
}
@media (max-width: 960px) {
  .border-right-md {
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 20px;
    margin-bottom: 20px;
  }
}
</style>
